//カード編集画面-デザインタブ
import React, { useState } from 'react'
import { Grid, Box, FormControlLabel, Checkbox, TextField, MenuItem } from '@material-ui/core'
import { nanoid } from 'nanoid';
import * as Linq from 'linq';

import { CardDb } from '../redux/cardDbSlice';
import { isMultiAttribute, isTableAttribute, isNondisplayAttribute, CardAttributeDb, CardAttributeDbHashType } from '../redux/cardAttributeDbSlice';
import { CardShapeDb } from '../redux/cardShapeDbSlice';
import { AttributeKinds, defaultFont, defaultFrame } from '../redux/cardAttributeModelIndex';
import useWindowSize from '@rehooks/window-size'
import useElementDimensions from '../hooks/useElementDimensions';
import { deepCopy, FullSizeImageAddType } from '../general';
import { initial } from './index';
import ItemMenu from '../components/ItemMenu'
import ItemDesignArea from '../components/ItemDesignArea';
import SelectLanguage from '../components/SelectLanguage'
import RenderCard from '../components/RenderCard'
import { EditCardShapeDbHashType } from '../components/EditDialog';
import ImageBrowserDialog from '../components/ImageBrowserDialog'
import ChangeAttributeOrderDialog from '../components/ChangeAttributeOrderDialog';
import DrawToolDialog from '../components/DrawToolDialog';
import SetCardCustomSizeDialog from '../components/SetCardCustomSizeDialog';
import ColorPalette from '../components/ColorPalette';

const isAttribute = (obj: any): obj is CardAttributeDb => {
  return typeof obj?.cardAttributeModel === 'string';
}
const isShape = (obj: any): obj is CardShapeDb => {
  return typeof obj?.kind === 'string';
}

//デザインタブ-カード表示
type CardDesignAreaProps = {
  card: CardDb;
  attributes: CardAttributeDbHashType;
  shapes: EditCardShapeDbHashType;
  changeItem: (item: CardAttributeDb | CardShapeDb | null) => void;
  selectingId: string | undefined;
  setSelectingId: (id: string | undefined) => void;
  width?: number;
  height?: number;
  changeTab: () => void;
}
const CardDesignArea: React.FC<CardDesignAreaProps> = ({ card, attributes, shapes, changeItem, selectingId, setSelectingId, width, height, changeTab }) => {
  const windowSize = useWindowSize()
  const workWidth = width ? width - 16 : (windowSize.innerWidth - (64 * 2)) * 0.6;
  const workHeight = height ? height : (windowSize.innerHeight - (64 * 2)) * 0.5;
  const widthScale = workWidth / card.defaultWidth;
  const heightScale = workHeight / card.defaultHeight;
  const scaleValue = Math.min(widthScale, heightScale);

  return (
    <Box ml={1}>
      <RenderCard card={card} attributes={attributes} shapes={shapes}
                  selectingId={selectingId} setSelectingId={setSelectingId} 
                  scaleValue={scaleValue} workHeight={workHeight}
                  changeItem={changeItem}
                  changeTab={changeTab}
      />
    </Box>
  );
}

type DesignCardProps = {
  cardId: string;
  card: CardDb;
  setCard: (card: CardDb) => void;
  attributes: CardAttributeDbHashType;
  setAttributes: (items: CardAttributeDbHashType) => void;
  shapes: EditCardShapeDbHashType;
  setShapes: (shapes: EditCardShapeDbHashType) => void;
  splitUpdated: boolean;
}
//デザインタブ
const DesignCard: React.FC<DesignCardProps> = ({ cardId, card, setCard, attributes, setAttributes, shapes, setShapes, splitUpdated }) => {
  const windowSize = useWindowSize()
  const workHeight = windowSize.innerHeight - (64 * 2) - 100
  const attributeIds = Object.keys(attributes).filter(id => attributes[id]);
  const shapeIds = Object.keys(shapes).filter(id => shapes[id]);
  const ids = attributeIds.concat(shapeIds);
  const [selectingId, setSelectingId] = useState<string | undefined>(undefined);
  const [languageIndex, setLanguageIndex] = useState(0)//言語設定
  type ImageBrowserActionType = { show: boolean; type?: 'attribute' | 'shape' }
  const [imageBrowserAction, setImageBrowserAction] = useState<ImageBrowserActionType>({ show: false });
  type DrawToolActionType = { show: boolean; type?: 'attribute' | 'shape' }
  const [drawToolAction, setDrawToolAction] = useState<DrawToolActionType>({ show: false });
  const [changeAttributeOrderAction, setChangeAttributeOrderAction] = useState(false);
  const [itemDesignTab, setItemDesignTab] = useState(0);
  const [sizeCustomMenuOpen, setSizeCustomMenuOpen] = useState<boolean>(false);
  const cardDesignAreaId = `card-design-area-${cardId}`;
  const { width, height } = useElementDimensions(cardDesignAreaId, splitUpdated);

  const heightInVertical = 270;
  type CardSizeType = 'horizontal' | 'vertical' | 'custom'
  const getCardSizeSelectValue = (): CardSizeType => {
    if (card.defaultWidth === initial.cardWidth && card.defaultHeight === initial.cardHeight) return 'horizontal';
    else if (card.defaultWidth === initial.cardWidth && card.defaultHeight === 270) return 'vertical';
    else return 'custom';
  }
  const changeCardSize = (size: unknown) => {
    const isProps = (value: unknown): value is { width: number; height: number; } => {
      if (!value) return false;
      return Boolean(value) && typeof value === 'object' &&
        'width' in value && typeof value.width === 'number' &&
        'height' in value && typeof value.height === 'number';
    }
    const newTemplate = deepCopy(card);
    if (size === 'horizontal') {
      newTemplate.defaultWidth = initial.cardWidth;
      newTemplate.defaultHeight = initial.cardHeight;
    } else if (size === 'vertical') {
      newTemplate.defaultWidth = initial.cardWidth;
      newTemplate.defaultHeight = heightInVertical;
    } else if (isProps(size)) {
      newTemplate.defaultWidth = size.width;
      newTemplate.defaultHeight = size.height;
    }
    updateCardCurrentHeight(attributes, newTemplate);
  }
  const changeGroup = (value: boolean) => {
    const newCard = deepCopy(card);
    newCard.isGroup = value;
    setCard(newCard);
  }
  const changeFrameDisplay = (display: boolean) => {
    const newCard = deepCopy(card);
    newCard.frame.display = display;
    setCard(newCard);
  }
  const changeFrameColor = (color: string) => {
    const newCard = deepCopy(card);
    newCard.frame.color = color;
    setCard(newCard);
  }
  const changeBackgroundDisplay = (display: boolean) => {
    const newCard = deepCopy(card);
    newCard.background.display = display;
    setCard(newCard);
  }
  const changeBackgroundColor = (color: string) => {
    const newCard = deepCopy(card);
    newCard.background.color = color;
    setCard(newCard);
  }
  const changeShadowDisplay = (display: boolean) => {
    const newCard = deepCopy(card);
    newCard.shadow = display;
    setCard(newCard);
  }
  const addNewAttribute = (kind: AttributeKinds, image?: FullSizeImageAddType) => {
    //選択された入力項目の初期値をカードに追加する
    const newAttributes = deepCopy(attributes);
    const newId = 'newAttribute:' + nanoid();
    const newAttribute: CardAttributeDbHashType = {};
    const commonProps = {
      cardId: cardId, languageId: 'ja',
      hint: '',
      frame: defaultFrame, order: attributeIds.length,
      x: 0, y: 20, z: ids.length, rotation: 0,
      description: {display: false, content: ''},
      background: {display: false, color: '#ffffff'}
    } as const;
    if (kind === 'single') {
      newAttribute[newId] = {
        ...commonProps,
        cardAttributeModel: 'single', name: 'new single',
        width: 200, height: 30,
        font: defaultFont, content: '', currentY: 20,
        defaultContent: ''
      };
      Object.assign(newAttributes, newAttribute);
    } else if (kind === 'multi') {
      newAttribute[newId] = {
        ...commonProps,
        cardAttributeModel: 'multi', name: 'new multi',
        width: 200, height: 30,
        font: initial.multiAttributeFont, content: '',
        currentY: 20, defaultContent: '',
        isMarkdown: false
      };
      Object.assign(newAttributes, newAttribute);
    } else if (kind === 'table') {
      newAttribute[newId] = {
        ...commonProps,
        cardAttributeModel: 'table', name: '表項目', hint: 'ダイアログが開きます',
        width: 100, height: 60,
        font: defaultFont, columnNames: {}, content: {}, currentY: 20,
      };
      Object.assign(newAttributes, newAttribute);
    } else if (kind === 'image' && image) {
      newAttribute[newId] = {
        ...commonProps,
        cardAttributeModel: 'image', name: 'new image',
        width: 60, height: 60,
        defaultImage: {
          imageId: image.id,
          originWidth: image.width,
          originHeight: image.height,
          trimXFrom: 0,
          trimYFrom: 0,
          trimXTo: image.width,
          trimYTo: image.height
        },
        content: {
          imageId: image.id,
          originWidth: image.width,
          originHeight: image.height,
          trimXFrom: 0,
          trimYFrom: 0,
          trimXTo: image.width,
          trimYTo: image.height
        },
        currentY: 20,
      };
      Object.assign(newAttributes, newAttribute);
    } else if (kind === 'nondisplay') {
      newAttribute[newId] = {
        ...commonProps,
        cardAttributeModel: 'nondisplay', name: 'new nondisplay',
        width: 200, height: 30,
        content: '', defaultContent: ''
      };
      Object.assign(newAttributes, newAttribute);
    } else return;

    setAttributes(newAttributes);
    setSelectingId(newId);
  }
  const addNewShape = (kind: string, image?: FullSizeImageAddType) => {
    const newShapes = deepCopy(shapes);
    const newId = 'newShape:' + nanoid();
    const newShape: EditCardShapeDbHashType = {};
    const commonProps = {
      cardId: cardId,
      x: 0, y: 20, z: ids.length, rotation: 0,
      frame: defaultFrame,
      background: {display: false, color: '#ffffff'}
    } as const;
    if (kind === 'text') {
      newShape[newId] = {
        ...commonProps,
        kind: 'text', name: 'new text',
        width: 100, height: 30,
        text: '入力してください', font: initial.textShapeFont
      };
      Object.assign(newShapes, newShape);
    } else if (kind === 'circle') {
      newShape[newId] = {
        ...commonProps,
        kind: 'circle', name: 'new circle',
        width: 50, height: 50,
        stroke: {display: true, color: '#000000', opacity: 1, width: 1},
        fill: {display: false, color: '#000000', opacity: 1}
      };
      Object.assign(newShapes, newShape);
    } else if (kind === 'rectangle') {
      newShape[newId] = {
        ...commonProps,
        kind: 'rectangle', name: 'new rectangle',
        width: 100, height: 30,
        stroke: {display: true, color: '#000000', opacity: 1, width: 1},
        fill: {display: false, color: '#000000', opacity: 1}
      };
      Object.assign(newShapes, newShape);
    } else if (kind === 'balloon') {
      newShape[newId] = {
        ...commonProps,
        kind: 'balloon', name: 'new balloon',
        width: 100, height: 60,
        stroke: {display: true, color: '#000000', opacity: 1, width: 1},
        fill: {display: false, color: '#000000', opacity: 1}
      };
      Object.assign(newShapes, newShape);
    } else if (kind === 'reverseBalloon') {
      newShape[newId] = {
        ...commonProps,
        kind: 'balloon', name: 'new balloon',
        width: -100, height: 60,
        stroke: {display: true, color: '#000000', opacity: 1, width: 1},
        fill: {display: false, color: '#000000', opacity: 1}
      };
      Object.assign(newShapes, newShape);
    } else if (kind === 'image' && image) {
      newShape[newId] = {
        ...commonProps,
        kind: 'image', name: 'new static image',
        width: 60, height: 60,
        image: {
          imageId: image.id,
          originWidth: image.width,
          originHeight: image.height,
          trimXFrom: 0,
          trimYFrom: 0,
          trimXTo: image.width,
          trimYTo: image.height
        }
      };
      Object.assign(newShapes, newShape);
    }

    setShapes(newShapes);
    setSelectingId(newId);
  }
  const updateCardCurrentHeight = (items: CardAttributeDbHashType, newCardValue?: CardDb) => {
    const newCard = newCardValue ? deepCopy(newCardValue) : deepCopy(card);
    let cardHeight = newCard.defaultHeight;
    const bottomY = Linq.from(attributeIds).max(id => {
      const item = items[id];
      if (isNondisplayAttribute(item)) return 0;
      const itemHeight = (isMultiAttribute(item) || isTableAttribute(item)) && item.currentHeight ? item.currentHeight : item.height;
      return item.currentY + itemHeight;
    });
    if (bottomY !== cardHeight) {
      newCard.currentHeight = Math.max(bottomY, newCard.defaultHeight);
      setCard(newCard);
    } else if (newCardValue) setCard(newCardValue);
  }
  const changeAttributesCurrentY = (items: CardAttributeDbHashType): CardAttributeDbHashType => {
    const newAttributes = deepCopy(items);
    const idsOrderedByY = Linq.from(attributeIds).orderBy(id => items[id].y).toArray();
    if (idsOrderedByY.length === 0) return items;
    if (idsOrderedByY.length === 1) {
      const targetId = idsOrderedByY[0];
      const target = newAttributes[targetId];
      if (isNondisplayAttribute(target)) return newAttributes;
      newAttributes[targetId] = { ...target, currentY: target.y };
      return newAttributes;
    }
    idsOrderedByY.forEach((id, ix) => {
      const attribute = newAttributes[id];
      if (isNondisplayAttribute(attribute)) return;
      let anchor: { id: string | undefined, y: number } = { id: undefined, y: 0 };
      for (let i = 0; i < ix; i++) {
        const targetId = idsOrderedByY[i]
        const target = newAttributes[targetId];
        if (isNondisplayAttribute(target)) break;
        const diff = attribute.y - (target.y + target.height);
        const targetHeight = (isMultiAttribute(target) || isTableAttribute(target)) && target.currentHeight ? target.currentHeight : target.height;
        const targetBottomY = target.currentY + targetHeight + diff;
        if (target.x < attribute.x + attribute.width && attribute.x < target.x + target.width && targetBottomY > anchor.y) {
          anchor = { id: targetId, y: targetBottomY };
        }
      }
      if (typeof anchor.id !== 'string') {
        attribute.currentY = attribute.y;
        return;
      }
      attribute.currentY = anchor.y;
      newAttributes[id] = attribute;
    })
    return newAttributes;
  }
  const changeItem = (item: CardAttributeDb | CardShapeDb | null, id?: string) => {
    if (selectingId === undefined) return;
    const targetId = id ? id : selectingId;
    const newAttributes = deepCopy(attributes);
    const newShapes = deepCopy(shapes);
    if (isAttribute(item)) {
      newAttributes[targetId] = item;
      const updatedAttributes = changeAttributesCurrentY(newAttributes);
      setAttributes(updatedAttributes);
      updateCardCurrentHeight(updatedAttributes);
    } else if (isShape(item)) {
      newShapes[targetId] = item;
      setShapes(newShapes);
    }
  }

  const changeTab = () => {
    setItemDesignTab(1);
  }

  return(
    <>
      <Box>
        <Grid container spacing={0}>
          <Grid item xs={8}>
            <Grid container spacing={0}>
              <Grid item xs={1}>
                <Box style={{height: workHeight, overflow: 'auto'}}>
                  <ItemMenu cardKind='common' 
                            onAddAttribute={addNewAttribute} onAddShape={addNewShape}
                            onImageBrowser={(type) => setImageBrowserAction({ show: true, type: type })}
                            onDrawTool={(type) => setDrawToolAction({ show: true, type: type })} />
                </Box>
              </Grid>
              <Grid item xs={11}>
                <Box style={{height: workHeight, overflow: 'auto'}}>
                  <Grid item xs={12} id={cardDesignAreaId} style={{marginBottom: '24px'}}>
                    <CardDesignArea card={card}
                      attributes={attributes}
                      shapes={shapes} changeItem={changeItem}
                      selectingId={selectingId}
                      setSelectingId={setSelectingId}
                      width={width} height={height}
                      changeTab={changeTab}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container alignItems="center">
                      <TextField
                        select variant="outlined"
                        value={getCardSizeSelectValue()}
                        onChange={ev => changeCardSize(ev.target.value)}
                        label="サイズ"
                      >
                        <MenuItem value="horizontal">横（{initial.cardWidth} × {initial.cardHeight}）</MenuItem>
                        <MenuItem value="vertical">縦（{initial.cardWidth} × {heightInVertical}）</MenuItem>
                        { getCardSizeSelectValue() === 'custom' &&
                          <MenuItem value="custom">{card.defaultWidth} × {card.defaultHeight}</MenuItem>
                        }
                        <MenuItem onClick={() => setSizeCustomMenuOpen(true)}>カスタム...</MenuItem>
                      </TextField>
                      <FormControlLabel label="グルーピングカードにする" style={{ marginLeft: 8 }}
                        control={
                          <Checkbox checked={card.isGroup} color="primary"
                            onChange={(ev) => changeGroup(ev.currentTarget.checked)} />
                        } />
                    </Grid>
                  </Grid>
                  <Grid container item xs={12}>
                    <Grid item alignItems="center" xs={12} md={4}>
                      <Grid container alignItems="center">
                        <FormControlLabel label="枠線表示"
                          control={
                            <Checkbox checked={card.frame.display} color="primary"
                              onChange={(ev) => changeFrameDisplay(ev.currentTarget.checked)} />
                          } />
                          <ColorPalette
                            currentValue={card.frame.color}
                            onChange={changeFrameColor}
                            disabled={!card.frame.display}
                          />
                        </Grid>
                    </Grid>
                    <Grid item alignItems="center" xs={12} md={4}>
                      <Grid container alignItems="center">
                        <FormControlLabel label="背景表示"
                          control={
                            <Checkbox checked={card.background.display} color="primary"
                              onChange={(ev) => changeBackgroundDisplay(ev.currentTarget.checked)} />
                          } />
                        <ColorPalette
                          currentValue={card.background.color}
                          onChange={changeBackgroundColor}
                          disabled={!card.background.display}
                        />
                      </Grid>
                    </Grid>
                    <Grid item alignItems="center" xs={12} md={4}>
                      <FormControlLabel label="影あり"
                        control={
                          <Checkbox checked={card.shadow} color="primary"
                            onChange={(ev) => changeShadowDisplay(ev.currentTarget.checked)} />
                        } />
                    </Grid>
                    {/* <Grid item xs={12}>
                      <SelectLanguage languageIndex={languageIndex} setLanguageIndex={setLanguageIndex} />
                    </Grid> */}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4}>
            <ItemDesignArea ids={ids}
              allItems={{attributes, setAttributes, shapes, setShapes, changeItem}}
              selectingId={selectingId}
              setSelectingId={setSelectingId}
              onChangeAttributeOrder={() => setChangeAttributeOrderAction(true)}
              tabValue={itemDesignTab}
              setTabValue={setItemDesignTab}
            />
          </Grid>
        </Grid>
      </Box>
      <ImageBrowserDialog 
        open={imageBrowserAction.show}
        onAddAttribute={(kind, image) => {
          setImageBrowserAction({ show: false });
          if (imageBrowserAction.type === 'attribute') addNewAttribute(kind, image);
          else if (imageBrowserAction.type === 'shape') addNewShape(kind, image);
        }}
        onClose={() => setImageBrowserAction({show: false})}
      />
      <DrawToolDialog open={drawToolAction.show} onClose={() => setDrawToolAction({ show: false })}
        onSave={(image: FullSizeImageAddType) => {
          setDrawToolAction({ show: false });
          if (drawToolAction.type === 'attribute') addNewAttribute('image', image);
          else if (drawToolAction.type === 'shape') addNewShape('image', image);
        }}
      />
      <ChangeAttributeOrderDialog open={changeAttributeOrderAction}
        onClose={() => setChangeAttributeOrderAction(false)}
        attributeIds={attributeIds} attributes={attributes}
        setAttributes={setAttributes} 
      />
      <SetCardCustomSizeDialog open={sizeCustomMenuOpen}
        onClose={() => setSizeCustomMenuOpen(false)}
        changeCardSize={(size) => changeCardSize(size)}
      />
    </>
  );
}

export default DesignCard;
